import React, { useState, FC, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useId } from '@reach/auto-id'
import cx from 'classnames'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'
import styles from './TextInput.module.scss'

type TextInputProps = {
  defaultValue?: string | number
  name: string
  type?: 'text' | 'password' | 'number' | 'email'
  value?: string | number
  label: string
  error?: string
  required?: boolean
  readOnly?: boolean
  disabled?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  autoComplete?: string
}

export const TextInput: FC<TextInputProps> = ({ type = 'text', value, label, error, ...rest }) => {
  const { t } = useTranslation()
  const inputId = useId()
  const [inputType, setInputType] = useState(type)
  const isPasswordInput = type === 'password'
  const isShowingPassword = isPasswordInput && inputType !== 'password'
  const iconName = isShowingPassword ? 'eye-off-line' : 'eye-line'
  const buttonLabel = isShowingPassword ? t('password.hide') : t('password.show')
  const inputStyles = cx({
    [styles.textInput]: true,
    [styles.filled]: value,
    [styles.errored]: error,
  })

  const toggleInputType = () => {
    const newInputType = inputType === 'text' ? 'password' : 'text'
    setInputType(newInputType)
  }

  return (
    <div className={inputStyles}>
      <input type={inputType} value={value} id={inputId} {...rest} />

      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>

      {error ? (
        <Text size="xsmall" className={styles.error}>
          <Icon name="alert-line" size="lg" /> {error}
        </Text>
      ) : null}

      {isPasswordInput && (
        <button
          onClick={toggleInputType}
          className={styles.toggleButton}
          type="button"
          aria-label={buttonLabel}
        >
          <Icon name={iconName} />
        </button>
      )}
    </div>
  )
}

TextInput.displayName = 'TextInput'
